import styled, { css } from "styled-components";
import { FC, MouseEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { AssignTopicsStatus } from "@/utils/enums";
import { api } from "@/utils/api";
import useAlerts from "@/hooks/useAlerts";

const CheckMark = styled.svg``;

const UndoIcon = styled.svg`
  display: none;
`;

const Button = styled.button<{ $status?: AssignTopicsStatus }>`
  border: 1px solid transparent;
  background-color: ${({ theme }) => theme.colors.grayscaleLight};
  svg {
    color: ${({ theme }) => theme.colors.gray};
  }
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 12px;
  transition: border-color ease-out 0.2s, background-color ease-out 0.2s;
  &:disabled {
    cursor: not-allowed;
  }
  ${({ theme, $status }) =>
    $status === AssignTopicsStatus.ASSIGNED &&
    css`
      background-color: ${theme.colors.warningLight};
      &:hover,
      &:active {
        border-color: ${theme.colors.warning};
      }
      ${CheckMark} {
        color: ${theme.colors.warning};
      }
    `}
  ${({ theme, $status }) =>
    $status === AssignTopicsStatus.CONFIRMED &&
    css`
      background-color: ${theme.colors.successLight};
      &:hover,
      &:active {
        border-color: ${theme.colors.warning};
        background-color: ${theme.colors.warningLight};
        ${CheckMark} {
          display: none;
        }
        ${UndoIcon} {
          display: block;
          color: ${theme.colors.warning};
        }
      }
      ${CheckMark} {
        color: ${theme.colors.success};
      }
    `}
`;

interface Props {
  status: AssignTopicsStatus;
  videoId: number;
}

const StatusButton: FC<Props> = ({ status, videoId }) => {
  const queryClient = useQueryClient();

  const alerts = useAlerts();

  const onStatusChange = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      await api.put<never, { id: number; status: AssignTopicsStatus }>(
        `/v1/videos/unassigned/${videoId}/topics-status`,
        {
          topics_status:
            status === AssignTopicsStatus.ASSIGNED
              ? AssignTopicsStatus.CONFIRMED
              : AssignTopicsStatus.ASSIGNED,
        }
      );

      queryClient.invalidateQueries(["unassigned_videos"], { exact: false });
    } catch (err) {
      alerts.error();
    }
  };

  return (
    <Button
      disabled={status === AssignTopicsStatus.DEFAULT}
      $status={status}
      onClick={onStatusChange}
    >
      <CheckMark
        width="8"
        height="6"
        viewBox="0 0 8 6"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M7 1L2.99998 5L1 3"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </CheckMark>
      <UndoIcon
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M8.19321 1.20188C7.24423 0.808799 6.20001 0.705952 5.19258 0.906342C4.18515 1.10673 3.25977 1.60136 2.53345 2.32767L2.09998 2.76115V2.14812C2.09998 1.73391 1.76419 1.39812 1.34998 1.39812C0.935762 1.39812 0.599976 1.73391 0.599976 2.14812L0.599976 4.57181L0.599976 4.57182C0.599976 4.67432 0.620537 4.77202 0.657762 4.86101C0.692625 4.94455 0.743263 5.02306 0.809676 5.09199C0.816339 5.09891 0.823135 5.1057 0.830058 5.11236C0.964879 5.24207 1.14811 5.32182 1.34998 5.32182H3.77367C4.18788 5.32182 4.52367 4.98603 4.52367 4.57182C4.52367 4.1576 4.18788 3.82182 3.77367 3.82182H3.16063L3.59411 3.38833C4.11065 2.8718 4.76876 2.52003 5.48522 2.37752C6.20167 2.23501 6.9443 2.30815 7.61919 2.5877C8.29407 2.86725 8.87091 3.34064 9.27675 3.94802C9.68259 4.55541 9.89921 5.2695 9.89921 5.99999C9.89921 6.73048 9.68259 7.44457 9.27675 8.05195C8.87091 8.65933 8.29407 9.13273 7.61919 9.41228C6.9443 9.69183 6.20167 9.76497 5.48522 9.62246C4.76876 9.47995 4.11065 9.12818 3.59411 8.61164C3.30122 8.31875 2.82635 8.31875 2.53345 8.61164C2.24056 8.90454 2.24056 9.37941 2.53345 9.6723C3.25977 10.3986 4.18515 10.8932 5.19258 11.0936C6.20001 11.294 7.24424 11.1912 8.19321 10.7981C9.14219 10.405 9.95329 9.73936 10.524 8.88531C11.0946 8.03125 11.3992 7.02715 11.3992 5.99999C11.3992 4.97282 11.0946 3.96873 10.524 3.11467C9.95329 2.26061 9.14219 1.59496 8.19321 1.20188Z"
          fill="currentColor"
        />
      </UndoIcon>
    </Button>
  );
};

export default StatusButton;
